import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import PinDots from '../PinDots/PinDots';
import PinPage from '../PinPage/PinPage';
import PinPad from '../PinPad/PinPad';
import { usePinEntry } from '../usePinEntry/usePinEntry';
import './CreatePasscode.css';

const HANDOFF_COPY_DELAY_MS = 120;
const HANDOFF_NAV_DELAY_MS = 360;
const INTRO_TRANSITION = {
  duration: 0.16,
  ease: [0.22, 1, 0.36, 1],
};
function delay(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function CreatePasscode() {
  const navigate = useNavigate();
  const { pin } = useContext(TranslationContext);
  const introCopy = useMemo(() => ({
    create: {
      title: pin.createTitle,
      subtitle: pin.createSubtitle,
    },
    confirm: {
      title: pin.confirmTitle,
      subtitle: pin.confirmSubtitle,
    },
  }), [pin]);
  const isMountedRef = useRef(true);
  const [isCompleting, setIsCompleting] = useState(false);
  const [copyStep, setCopyStep] = useState('create');
  const [isHandoffActive, setIsHandoffActive] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleComplete = useCallback(
    async (passcode) => {
      setIsCompleting(true);
      setIsHandoffActive(true);
      await delay(HANDOFF_COPY_DELAY_MS);

      if (!isMountedRef.current) {
        return true;
      }

      setCopyStep('confirm');
      await delay(HANDOFF_NAV_DELAY_MS);

      if (!isMountedRef.current) {
        return true;
      }

      navigate('/onboarding/passcode/confirm', { state: { passcode } });
      return true;
    },
    [navigate]
  );

  const { digits, append, remove } = usePinEntry(4, handleComplete);

  return (
    <PinPage className="create-passcode">
      <div className="pin-page__body">
        <div className="pin-page__intro">
          <motion.div
            key={copyStep}
            initial={copyStep === 'create' ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={INTRO_TRANSITION}
          >
            <h1 className="pin-page__title">{introCopy[copyStep].title}</h1>
            <p className="pin-page__subtitle">{introCopy[copyStep].subtitle}</p>
          </motion.div>
        </div>

        <PinDots length={4} filledCount={digits.length} isHandoff={isHandoffActive} />
        <PinPad
          onDigit={append}
          onDelete={remove}
          showDelete={digits.length > 0}
          disabled={isCompleting}
        />
      </div>
    </PinPage>
  );
}

export default CreatePasscode;
